import React, { useMemo, useState } from 'react';
import DatePicker from 'react-datepicker';
import { Form, Icon } from 'semantic-ui-react';
import { useForm, Controller } from 'react-hook-form';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getWithAuth } from 'src/api';
import { makePlural } from 'src/utils/useful_functions';

const dayMultiplier = 24 * 60 * 60 * 1000;

type SortDirection = 'asc' | 'desc';
type SortableColumn =
  | 'timestamp'
  | 'project_id'
  | 'ref_id'
  | 'email'
  | 'action'
  | 'feature_kind';

function dateDifferenceDays(start, end) {
  const diffTime = Math.abs(end - start);
  return Math.ceil(diffTime / dayMultiplier);
}

// 1 day
const defaultStart = new Date(new Date().getTime() - 1 * dayMultiplier);

const DEFAULT_DATES = {
  //   startDate: new Date('1 Jan 2020'),
  startDate: defaultStart,
  endDate: new Date(),
  projectId: '',
  refId: '',
  email: '',
  action: '',
  featureKind: '',
  days: dateDifferenceDays(defaultStart, new Date()),
};

const ACTION_OPTIONS = [
  { key: 'ACTION_NULL', text: '', value: null },
  { key: 'CREATE', text: 'Create', value: 1 },
  { key: 'DELETE', text: 'Delete', value: 2 },
];
const FEATURE_KIND_OPTIONS = [
  { key: 'FEATURE_NULL', text: '', value: null },
  { key: 'PROJECT', text: 'Project', value: 1 },
  { key: 'NETWORK_SITE', text: 'Network Site', value: 2 },
  { key: 'SUBSCRIBER_SITE', text: 'Subscriber Site', value: 3 },
  { key: 'PTP_LINK', text: 'PTP Link', value: 4 },
  { key: 'ACCESS_POINT', text: 'Access Point', value: 5 },
  { key: 'MESH_LINK', text: 'Mesh Link', value: 6 },
  { key: 'PMP_LINK', text: 'PMP Link', value: 7 },
  { key: 'VIEWSHED', text: 'Viewshed', value: 8 },
  { key: 'LIDAR_VIEWSHED', text: 'Lidar Viewshed', value: 9 },
  { key: 'GEOMETRY_LAYER_POINT', text: 'Geometry Layer - Point', value: 10 },
  { key: 'GEOMETRY_LAYER_LINE', text: 'Geometry Layer - Line', value: 11 },
  {
    key: 'GEOMETRY_LAYER_POLYGON',
    text: 'Geometry Layer - Polygon',
    value: 12,
  },
  {
    key: 'GEOMETRY_LAYER_OVERLAY',
    text: 'Geometry Layer - Overlay',
    value: 13,
  },
];

const ACTION_REVERSE = Object.fromEntries(
  ACTION_OPTIONS.map((action) => [action.value, action.text])
);
const FEATURE_KIND_REVERSE = Object.fromEntries(
  FEATURE_KIND_OPTIONS.map((fk) => [fk.value, fk.text])
);

function safeParam([k, v]) {
  return `${k}=${encodeURIComponent(v)}`;
}

function getParams(params) {
  return `?${Object.entries(params).map(safeParam).join('&')}`;
}

export function TrackingStats() {
  const { control, setValue, getValues, watch, handleSubmit, resetField } =
    useForm({
      defaultValues: DEFAULT_DATES,
    });

  let {
    startDate,
    endDate,
    days,
    projectId,
    refId,
    email,
    action,
    featureKind,
  } = watch();

  const queryKey = [
    'tracking-stats',
    startDate,
    endDate,
    projectId,
    refId,
    email,
    action,
    featureKind,
  ];

  const { data: trackingData, isFetching } = useQuery({
    queryKey,
    queryFn: async () => {
      let { startDate, endDate, projectId, refId, email, action, featureKind } =
        getValues();
      let params = {
        // start: null,
        // end: null,
        // project_id: null,
        // ref_id: null,
        // email: null,
        // action: null,
        // feature_kind: null,
      };
      if (startDate) {
        params.start = startDate.toISOString();
      }
      if (endDate) {
        params.end = endDate.toISOString();
      }
      if (projectId) {
        params.project_id = projectId;
      }
      if (refId) {
        params.ref_id = refId;
      }
      if (email) {
        params.email = email;
      }
      if (action) {
        params.action = action;
      }
      if (featureKind) {
        params.feature_kind = featureKind;
      }
      return await getWithAuth(`admin/tracking${getParams(params)}`);
    },
    initialData: null,
    enabled: true,
  });

  const qc = useQueryClient();

  const onSubmit = () => {
    qc.removeQueries({
      queryKey: queryKey,
    });
  };

  const clickAndRefresh = (key, value) => {
    setValue(key, value);
  };

  const onDayChange = (newDays) => {
    const newStartDate = new Date(
      endDate.getTime() - (newDays - 1) * dayMultiplier
    );
    setValue('startDate', newStartDate);
  };

  return (
    <>
      <Form
        onSubmit={handleSubmit(onSubmit)}
        style={{
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Form.Group className="tracking-container">
          <div className="tracking-flex">
            <label>Start Date:</label>
            <Controller
              control={control}
              name="startDate"
              render={({ field: { onChange, onBlur, value } }) => (
                <DatePicker
                  onBlur={onBlur}
                  selected={value}
                  onChange={(newStartDate) => {
                    resetField('days', {
                      defaultValue: dateDifferenceDays(newStartDate, endDate),
                    });
                    onChange(newStartDate);
                  }}
                  dateFormat="dd/MM/yyyy"
                />
              )}
            />
          </div>
          <div className="tracking-flex">
            <label>End Date:</label>
            <Controller
              control={control}
              name="endDate"
              render={({ field: { onChange, onBlur, value } }) => (
                <DatePicker
                  onBlur={onBlur}
                  selected={value}
                  onChange={(newEndDate) => {
                    resetField('days', {
                      defaultValue: dateDifferenceDays(startDate, newEndDate),
                    });
                    onChange(newEndDate);
                  }}
                  dateFormat="dd/MM/yyyy"
                />
              )}
            />
          </div>
          <div>
            <label>Number of Days:</label>
            <Controller
              control={control}
              name="days"
              render={({ field: { onChange, onBlur, value } }) => (
                <Form.Input
                  type="number"
                  name="days"
                  defaultValue={days}
                  onBlur={onBlur}
                  selected={value}
                  onChange={(e, { value }) => {
                    if (value === '' || !/^[0-9]+$/.test(value)) {
                      setValue(
                        'startDate',
                        new Date(Date.now() - dayMultiplier)
                      );
                      return;
                    }
                    const newDays = parseInt(value);
                    onDayChange(newDays);
                    onChange(newDays);
                  }}
                />
              )}
            />
          </div>
          <div>
            <label>Project ID:</label>
            <Controller
              control={control}
              name="projectId"
              render={({ field: { onChange } }) => (
                <Form.Input
                  name="projectId"
                  value={projectId}
                  icon={{
                    name: 'cancel',
                    onClick: () => setValue('projectId', ''),
                    link: true,
                  }}
                  onChange={(e, { value }) => {
                    onChange(value);
                  }}
                />
              )}
            />
          </div>
          <div>
            <label>Ref ID:</label>
            <Controller
              control={control}
              name="refId"
              render={({ field: { onChange } }) => (
                <Form.Input
                  name="refId"
                  value={refId}
                  icon={{
                    name: 'cancel',
                    onClick: () => setValue('refId', ''),
                    link: true,
                  }}
                  onChange={(e, { value }) => {
                    onChange(value);
                  }}
                />
              )}
            />
          </div>
          <div>
            <label>Email:</label>
            <Controller
              control={control}
              name="email"
              render={({ field: { onChange } }) => (
                <Form.Input
                  name="email"
                  value={email}
                  icon={{
                    name: 'cancel',
                    onClick: () => setValue('email', ''),
                    link: true,
                  }}
                  onChange={(e, { value }) => {
                    onChange(value);
                  }}
                />
              )}
            />
          </div>
          <div>
            <label>Action:</label>
            <Controller
              control={control}
              name="action"
              render={({ field: { onChange } }) => (
                <Form.Select
                  name="action"
                  options={ACTION_OPTIONS}
                  value={action}
                  onChange={(e, { value }) => {
                    onChange(value);
                  }}
                />
              )}
            />
          </div>
          <div>
            <label>Feature Kind:</label>
            <Controller
              control={control}
              name="featureKind"
              render={({ field: { onChange } }) => (
                <Form.Select
                  name="featureKind"
                  value={featureKind}
                  options={FEATURE_KIND_OPTIONS}
                  onChange={(e, { value }) => {
                    onChange(value);
                  }}
                />
              )}
            />
          </div>
        </Form.Group>
      </Form>

      {!isFetching && (
        <TrackingTable
          trackingData={trackingData}
          clickAndRefresh={clickAndRefresh}
        />
      )}
    </>
  );
}

const COLUMN_HEADERS: Array<{ key: SortableColumn; label: string }> = [
  { key: 'timestamp', label: 'Timestamp' },
  { key: 'project_id', label: 'Project ID' },
  { key: 'ref_id', label: 'Ref ID' },
  { key: 'email', label: 'Email' },
  { key: 'action', label: 'Action' },
  { key: 'feature_kind', label: 'Feature Kind' },
];

const compareValues = (a: any, b: any, direction: SortDirection): number => {
  // Handle null/undefined values
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;

  // Normalize strings for case-insensitive comparison
  const valA = typeof a === 'string' ? a.toLowerCase() : a;
  const valB = typeof b === 'string' ? b.toLowerCase() : b;

  // Compare and apply direction
  if (valA < valB) return direction === 'asc' ? -1 : 1;
  if (valA > valB) return direction === 'asc' ? 1 : -1;
  return 0;
};

const getDisplayValue = (row: any, column: SortableColumn): any => {
  const value = row[column];

  // Convert action and feature_kind to their display text
  if (column === 'action') {
    return ACTION_REVERSE[value] || value;
  }
  if (column === 'feature_kind') {
    return FEATURE_KIND_REVERSE[value] || value;
  }

  return value;
};

const getSortIcon = (
  column: SortableColumn,
  sortColumn: SortableColumn | null,
  sortDirection: SortDirection
) => {
  if (sortColumn !== column) {
    return <Icon name="sort" style={{ opacity: 0.5, marginLeft: '0.3em' }} />;
  }
  return (
    <Icon
      name={sortDirection === 'asc' ? 'sort up' : 'sort down'}
      style={{ marginLeft: '0.3em' }}
    />
  );
};

const TrackingTable = ({ trackingData, clickAndRefresh }) => {
  const [sortColumn, setSortColumn] = useState<SortableColumn | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  const handleSort = (column: SortableColumn) => {
    if (sortColumn === column) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const sortedData = useMemo(() => {
    if (!sortColumn || !trackingData) return trackingData;

    return [...trackingData].sort((a, b) =>
      compareValues(
        getDisplayValue(a, sortColumn),
        getDisplayValue(b, sortColumn),
        sortDirection
      )
    );
  }, [trackingData, sortColumn, sortDirection]);

  return (
    <>
      <div className="tracking-table">
        <div className="tracking-header">
          {COLUMN_HEADERS.map(({ key, label }) => (
            <div
              key={key}
              className="tracking-cell-header"
              onClick={() => handleSort(key)}
              style={{ cursor: 'pointer' }}
            >
              {label}
              {getSortIcon(key, sortColumn, sortDirection)}
            </div>
          ))}
        </div>
        {sortedData.map((row, idx) => (
          <TrackingRow row={row} key={idx} clickAndRefresh={clickAndRefresh} />
        ))}
      </div>
      <div className="tracking-row-count">
        {`${trackingData.length} ${makePlural(
          trackingData.length,
          'Row',
          'Rows'
        )}`}
      </div>
    </>
  );
};

const TrackingRow = ({ row, clickAndRefresh }) => {
  return (
    <div className="tracking-row">
      <div className="tracking-cell">{row.timestamp}</div>
      <div className="tracking-cell">
        <a onClick={() => clickAndRefresh('projectId', row.project_id)}>
          {row.project_id}
        </a>
      </div>
      <div className="tracking-cell">
        <a onClick={() => clickAndRefresh('refId', row.ref_id)}>{row.ref_id}</a>
      </div>
      <div className="tracking-cell">
        <a onClick={() => clickAndRefresh('email', row.email)}>{row.email}</a>
      </div>
      <div className="tracking-cell">
        <a onClick={() => clickAndRefresh('action', row.action)}>
          {ACTION_REVERSE[row.action]}
        </a>
      </div>
      <div className="tracking-cell">
        <a onClick={() => clickAndRefresh('featureKind', row.feature_kind)}>
          {FEATURE_KIND_REVERSE[row.feature_kind]}
        </a>
      </div>
    </div>
  );
};
